import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import WorkcellMonthlyTurnoverChart from "../components/WorkcellMonthlyTurnoverChart";
import MonthlyThroughputChart from "../components/MonthlyThroughputChart";
import FullWidthLayout from "../components/FullWidthLayout";
import DateSelector from "../components/DateSelector";
import { Typography, Box, TextField } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import { fetchAllWorkcells } from "../api";
import WorkcellMonthlyPlannedVsDoneChart from "../components/WorkcellMonthlyPlannedVsDoneChart";
import WorkcellMonthlyOeeBarChart from '../components/WorkcellMonthlyOeeBarChart';
import { toMonthStart } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency } from "../context/CustomerSettingsContext";
import { formatLocalDate } from "../utils/localDateTime";

const WorkcellMonthlyOverviewPage = () => {
  const { workcell_id, date } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currency } = useCustomerCurrency();
  const rawDate = date || formatLocalDate(new Date());
  const selectedDate = toMonthStart(rawDate);
    useEffect(() => {
      if (!date || date !== selectedDate) {
        navigate(`/chart/workcell/${workcell_id}/monthly/${selectedDate}`, { replace: true });
      }
    }, [date, selectedDate, workcell_id, navigate]);

  const [workcells, setWorkcells] = useState([]);
  const [loadingWorkcells, setLoadingWorkcells] = useState(true);
  const [turnoverLoading, setTurnoverLoading] = useState(true);
  const [throughputLoading, setThroughputLoading] = useState(true);
  const [plannedVsDoneLoading, setPlannedVsDoneLoading] = useState(true);
  const [oeeLoading, setOeeLoading] = useState(true);

  // Reset loading states when date or workcell changes
  useEffect(() => {
    setTurnoverLoading(true);
    setThroughputLoading(true);
    setPlannedVsDoneLoading(true);
    setOeeLoading(true);
  }, [selectedDate, workcell_id]);

  // Fetch workcells for selector
  useEffect(() => {
    const fetchWorkcells = async () => {
      try {
        const data = await fetchAllWorkcells();
        const filtered = data.filter(w => w.has_oee);
        setWorkcells(filtered);
      } finally {
        setLoadingWorkcells(false);
      }
    };
    fetchWorkcells();
  }, []);

  const handleBlockClick = useCallback((clickedDate) => {
    // Format date as YYYY-MM-DD if needed
    const targetDate = clickedDate instanceof Date
      ? formatLocalDate(clickedDate)
      : clickedDate
    navigate(`/chart/workcell/${workcell_id}/daily/${targetDate}/`);
  }, [navigate, workcell_id]);

  const handleDateChange = useCallback((newDate) => {
    navigate(`/chart/workcell/${workcell_id}/monthly/${newDate}`);
  }, [navigate, workcell_id]);

  // Check if any data is still loading
  const isAnyLoading = turnoverLoading || throughputLoading || plannedVsDoneLoading || oeeLoading;

  return (
    <FullWidthLayout date={selectedDate}>
      <Box display="flex" alignItems="center" mb={2} mt={1}>
        <Autocomplete
          sx={{ minWidth: 260, mr: 2 }}
          size="small"
          disabled={loadingWorkcells}
          options={workcells}
          getOptionLabel={(option) => option.workcell_name || ''}
          isOptionEqualToValue={(option, value) => option.workcell_id === value.workcell_id}
          value={workcells.find(w => w.workcell_id === parseInt(workcell_id)) || null}
          onChange={(event, newValue) => {
            if (newValue && newValue.workcell_id) {
              navigate(`/chart/workcell/${newValue.workcell_id}/monthly/${selectedDate}`);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label={t("workcell.selector.label")} />
          )}
        />
      </Box>

      <Typography textAlign="center" gutterBottom>
        {t("workcell.monthlyOverview.intro")}
      </Typography>

      <DateSelector
        date={selectedDate}
        setDate={handleDateChange}
        disabled={isAnyLoading}
        mode="month" />

      <Typography variant="h4" gutterBottom mt={5}>{t("workcell.monthlyOverview.turnover.title", { currency })}</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t("workcell.monthlyOverview.turnover.description")}
      </Typography>
      <WorkcellMonthlyTurnoverChart
        date={selectedDate}
        workcellId={workcell_id}
        onClick={handleBlockClick}
        loading={turnoverLoading}
        onLoadingChange={setTurnoverLoading}
      />

      <Typography variant="h4" gutterBottom mt={5}>{t("workcell.monthlyOverview.throughput.title")}</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t("workcell.monthlyOverview.throughput.description")}
      </Typography>
      <MonthlyThroughputChart
        date={selectedDate}
        workcellId={workcell_id}
        onClick={handleBlockClick}
        loading={throughputLoading}
        onLoadingChange={setThroughputLoading}
      />

      <Typography variant="h4" gutterBottom mt={5}>{t("workcell.monthlyOverview.plannedVsDone.title")}</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t("workcell.monthlyOverview.plannedVsDone.description")}
      </Typography>
      <WorkcellMonthlyPlannedVsDoneChart
        date={selectedDate}
        workcellId={workcell_id}
        onClick={handleBlockClick}
        loading={plannedVsDoneLoading}
        onLoadingChange={setPlannedVsDoneLoading}
      />

      <Typography variant="h4" gutterBottom mt={5}>{t("workcell.monthlyOverview.oee.title")}</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {t("workcell.monthlyOverview.oee.description")}
      </Typography>
      <WorkcellMonthlyOeeBarChart
        date={selectedDate}
        workcellId={workcell_id}
        onClick={handleBlockClick}
        loading={oeeLoading}
        onLoadingChange={setOeeLoading}
      />
    </FullWidthLayout>
  );
};

export default WorkcellMonthlyOverviewPage;
